'use client'

import React, { useEffect, useRef, useState } from 'react'
import { TrendingUp, Activity, BarChart2, Waves } from 'lucide-react'

type IndicatorEntry = {
  icon: React.ComponentType<{ className?: string }>
  category: string
  subtitle: string
  description: string
  indicators: { name: string; shortcode: string; type: 'pane' | 'overlay' }[]
  image: string
}

const ENTRIES: IndicatorEntry[] = [
  {
    icon: Activity,
    category: 'Momentum',
    subtitle: 'Measure speed and strength of price moves',
    description:
      'Momentum indicators reveal the force behind a price move — not just direction. Spot divergences, exhaustion points, and regime shifts before they play out on the candles.',
    indicators: [
      { name: 'Balance of Power', shortcode: 'BOP', type: 'pane' },
      { name: 'MACD', shortcode: 'MACD', type: 'pane' },
      { name: 'Momentum', shortcode: 'Mom', type: 'pane' },
      { name: 'Rate of Change', shortcode: 'ROC', type: 'pane' },
      { name: 'Bull Bear Power', shortcode: 'BBP', type: 'pane' },
      { name: 'Relative Strength Index', shortcode: 'RSI', type: 'pane' },
      { name: 'Stochastic RSI', shortcode: 'StochRSI', type: 'pane' },
    ],
    image: '/previews/indicators-momentum.webp',
  },
  {
    icon: Waves,
    category: 'Volatility',
    subtitle: 'Map the envelope around price action',
    description:
      'Volatility indicators define the boundaries of normal price behaviour. Use them to detect squeezes before explosive moves, or to confirm when a breakout has real room to run.',
    indicators: [
      { name: 'Bollinger Bands', shortcode: 'BB', type: 'overlay' },
      { name: 'Keltner Channels', shortcode: 'KC', type: 'overlay' },
      { name: 'Donchian Channels', shortcode: 'DC', type: 'overlay' },
      { name: 'Average True Range', shortcode: 'ATR', type: 'pane' },
      { name: 'Historical Volatility', shortcode: 'HV', type: 'pane' },
      { name: 'Chaikin Volatility', shortcode: 'CV', type: 'pane' },
    ],
    image: '/previews/indicators-volatility.webp',
  },
  {
    icon: BarChart2,
    category: 'Volume',
    subtitle: 'Follow the money, not the noise',
    description:
      'Price without volume context is half the story. Volume indicators confirm trend conviction, expose distribution, and highlight the exact levels where institutional activity concentrates.',
    indicators: [
      { name: 'Volume Flow v3', shortcode: 'VFv3', type: 'overlay' },
      { name: 'Volume LinReg Trend', shortcode: 'VLRTrend', type: 'overlay' },
      { name: 'Weis Wave Volume', shortcode: 'WWV', type: 'pane' },
      { name: 'Volume Colored Bars', shortcode: 'VCBars', type: 'pane' },
      { name: 'On-Balance Volume', shortcode: 'OBV', type: 'pane' },
      { name: 'Cumulative Volume Delta', shortcode: 'CVD', type: 'pane' },
    ],
    image: '/previews/indicators-volume.webp',
  },
  {
    icon: TrendingUp,
    category: 'Trend',
    subtitle: 'Align with the direction of least resistance',
    description:
      'Trend indicators cut through intraday noise to reveal the dominant bias. Layer them with momentum and volume tools for high-conviction setups that Algo can analyse and act on in one click.',
    indicators: [
      { name: 'EMA Ribbon (8/13/21/34/55)', shortcode: 'EMA', type: 'overlay' },
      { name: 'VWAP', shortcode: 'VWAP', type: 'overlay' },
      { name: 'Supertrend', shortcode: 'ST', type: 'overlay' },
      { name: 'Parabolic SAR', shortcode: 'PSAR', type: 'overlay' },
      { name: 'Ichimoku Cloud', shortcode: 'Ichi', type: 'overlay' },
      { name: 'ADX / DMI', shortcode: 'ADX', type: 'pane' },
    ],
    image: '/previews/indicators-trend.webp',
  },
]

export function IndicatorsSection() {
  const [activeIndex, setActiveIndex] = useState(0)
  const sentinelRefs = useRef<(HTMLDivElement | null)[]>([])

  const setSentinelRef = (el: HTMLDivElement | null, i: number) => {
    sentinelRefs.current[i] = el
  }

  useEffect(() => {
    let frame = 0
    let last = -1

    const tick = () => {
      frame = requestAnimationFrame(tick)
      const centerY = window.innerHeight / 3
      let bestIndex = 0
      let bestDist = Infinity
      sentinelRefs.current.forEach((node, i) => {
        if (!node) return
        const rect = node.getBoundingClientRect()
        const dist = Math.abs(rect.top + rect.height / 2 - centerY)
        if (dist < bestDist) { bestDist = dist; bestIndex = i }
      })
      if (bestIndex !== last) { last = bestIndex; setActiveIndex(bestIndex) }
    }

    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
  }, [])

  return (
    <section
      className="bg-black py-16 md:py-32"
      style={{ fontFamily: 'var(--font-ppmori, PPMori), sans-serif' }}
    >
      <div className="mx-auto max-w-5xl px-6">
        <div className="mb-16 max-w-2xl">
          <h2 className="text-4xl font-medium text-white lg:text-5xl">
            More indicators than your exchange will ever give you.
          </h2>
          <p className="mt-4 text-lg text-white/50">
            400+ technical indicators across momentum, volatility, volume, and trend — all inside the same workspace where you trade.
          </p>
        </div>

        <div className="space-y-16 md:space-y-24">
          {ENTRIES.map((entry, index) => {
            const isActive = index === activeIndex
            const Icon = entry.icon

            return (
              <div
                key={entry.category}
                className="relative flex flex-col gap-6 md:flex-row md:gap-12"
              >
                {/* Sentinel for scroll tracking */}
                <div
                  ref={(el) => setSentinelRef(el, index)}
                  aria-hidden
                  className="absolute -top-20 left-0 h-8 w-8 opacity-0 pointer-events-none"
                />

                {/* Left: sticky label */}
                <div className="top-8 flex h-min w-52 shrink-0 items-start gap-3 md:sticky">
                  <div
                    className="flex items-center justify-center p-2 transition-colors duration-300"
                    style={{
                      background: isActive ? 'rgba(255,183,3,0.15)' : 'rgba(255,255,255,0.05)',
                      color: isActive ? '#ffb703' : 'rgba(255,255,255,0.3)',
                    }}
                  >
                    <Icon className="size-4" />
                  </div>
                  <div>
                    <div
                      className="text-sm font-medium transition-colors duration-300"
                      style={{ color: isActive ? '#fff' : 'rgba(255,255,255,0.4)' }}
                    >
                      {entry.category}
                    </div>
                    <div className="mt-0.5 text-xs text-white/30">{entry.subtitle}</div>
                  </div>
                </div>

                {/* Right: content card */}
                <div
                  className="flex flex-col border transition-colors duration-300"
                  style={{
                    borderColor: isActive ? 'rgba(255,183,3,0.25)' : 'rgba(255,255,255,0.08)',
                    background: isActive ? 'rgba(255,183,3,0.03)' : 'rgba(255,255,255,0.02)',
                  }}
                >
                  {/* Image placeholder */}
                  <div className="relative w-full overflow-hidden border-b border-white/8" style={{ aspectRatio: '16/7' }}>
                    <img
                      src={entry.image}
                      alt={`${entry.category} indicators`}
                      className="h-full w-full object-cover object-top"
                      onError={(e) => {
                        // placeholder while images are not yet added
                        (e.currentTarget as HTMLImageElement).style.display = 'none'
                      }}
                    />
                    {/* fallback background shown until image is added */}
                    <div className="absolute inset-0 bg-white/[0.02]" />
                  </div>

                  <div className="p-6 space-y-5">
                    <p className="text-sm text-white/60 leading-relaxed">{entry.description}</p>

                    {/* Indicator list */}
                    <div className="grid grid-cols-1 gap-px bg-white/5 sm:grid-cols-2">
                      {entry.indicators.map((ind) => (
                        <div
                          key={ind.shortcode}
                          className="flex items-center justify-between bg-black px-4 py-2.5"
                        >
                          <span className="text-sm text-white/70">{ind.name}</span>
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-xs text-white/30">{ind.shortcode}</span>
                            <span
                              className="text-xs px-1.5 py-0.5"
                              style={{
                                color: ind.type === 'overlay' ? '#4ade80' : '#60a5fa',
                                background: ind.type === 'overlay' ? 'rgba(74,222,128,0.08)' : 'rgba(96,165,250,0.08)',
                              }}
                            >
                              {ind.type}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}
